import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import { 
  Lightbulb, Package, DollarSign, Clock, User, Mail, 
  CheckCircle, XCircle, Loader2, Send, MessageSquare, RefreshCw
} from "lucide-react";

export const RecommendationsPanel = ({ open, onOpenChange }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRec, setSelectedRec] = useState(null);
  const [response, setResponse] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (open) {
      fetchRecommendations();
    }
  }, [open]);

  const fetchRecommendations = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/community/consultant/recommendations`);
      if (res.ok) {
        const data = await res.json();
        setRecommendations(data);
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async () => {
    if (!selectedRec || !response.trim() || !status) {
      toast.error("Please fill in response and select a status");
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/community/consultant/recommendations/${selectedRec.recommendation_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ response, status })
      });

      if (res.ok) {
        toast.success("Response sent successfully");
        setSelectedRec(null);
        setResponse("");
        setStatus("");
        fetchRecommendations();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send response");
      }
    } catch (error) {
      toast.error("Error sending response");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRecs = recommendations.filter(rec => {
    if (filter === "all") return true;
    return rec.status === filter;
  });

  const getCategoryIcon = (category) => {
    const icons = {
      "CRM": "💼",
      "Accounting": "📊",
      "Marketing": "📣",
      "Project Management": "📋",
      "E-commerce": "🛒",
      "Communication": "💬",
      "Design": "🎨",
      "Other": "📦"
    };
    return icons[category] || "📦";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
      case "responded":
        return <Badge className="bg-green-100 text-green-700">Responded</Badge>;
      case "closed":
        return <Badge className="bg-neutral-100 text-neutral-700">Closed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const pendingCount = recommendations.filter(r => r.status === "pending").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-500" />
            Software & Service Recommendations
            {pendingCount > 0 && (
              <Badge className="bg-amber-500 text-white ml-2">{pendingCount} pending</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Review and respond to member requests for software and service recommendations
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3 mb-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="responded">Responded</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchRecommendations}>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : filteredRecs.length === 0 ? (
            <div className="text-center py-12">
              <Lightbulb className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <p className="text-neutral-500">No recommendation requests yet</p>
              <p className="text-sm text-neutral-400 mt-1">
                Members can request recommendations from the Community Hub
              </p>
            </div>
          ) : (
            filteredRecs.map((rec) => (
              <Card 
                key={rec.recommendation_id} 
                className={`transition-colors ${
                  rec.status === "pending" 
                    ? "border-amber-200 bg-amber-50/50" 
                    : "border-neutral-200"
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getCategoryIcon(rec.category)}</span>
                      <div>
                        <h4 className="font-semibold text-neutral-900">{rec.category}</h4>
                        <div className="flex items-center gap-2 text-sm text-neutral-500">
                          <User className="w-3 h-3" />
                          <span>{rec.user_name}</span>
                          <span>•</span>
                          <Mail className="w-3 h-3" />
                          <span>{rec.user_email}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(rec.status)}
                      <span className="text-xs text-neutral-400">
                        {formatDate(rec.created_at)}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-3 border border-neutral-100 mb-3">
                    <p className="text-sm text-neutral-700">{rec.description}</p>
                    {rec.budget && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-green-600">
                        <DollarSign className="w-4 h-4" />
                        <span>Budget: {rec.budget}</span>
                      </div>
                    )}
                  </div>

                  {rec.response && (
                    <div className="bg-green-50 rounded-lg p-3 border border-green-100 mb-3">
                      <p className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" />
                        Your Response:
                      </p>
                      <p className="text-sm text-green-800">{rec.response}</p>
                      <p className="text-xs text-green-600 mt-1">
                        Responded on {formatDate(rec.responded_at)}
                      </p>
                    </div>
                  )}

                  {rec.status === "pending" && (
                    <Button 
                      size="sm" 
                      className="bg-amber-500 hover:bg-amber-600"
                      onClick={() => {
                        setSelectedRec(rec);
                        setResponse("");
                        setStatus("responded");
                      }}
                    >
                      <Send className="w-4 h-4 mr-1" />
                      Respond
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Response Dialog */}
        <Dialog open={!!selectedRec} onOpenChange={(open) => !open && setSelectedRec(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Respond to Request</DialogTitle>
              <DialogDescription>
                Provide a recommendation for {selectedRec?.user_name}'s {selectedRec?.category} request
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="bg-neutral-50 p-3 rounded-lg">
                <p className="text-sm text-neutral-600">{selectedRec?.description}</p>
                {selectedRec?.budget && (
                  <p className="text-sm text-green-600 mt-2">Budget: {selectedRec.budget}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Your Recommendation</label>
                <Textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  placeholder="Share your recommendation, including specific tools, pricing, and why you recommend them..."
                  rows={5}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="responded">Responded (member will be notified)</SelectItem>
                    <SelectItem value="closed">Closed (no notification)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedRec(null)}>
                Cancel
              </Button>
              <Button 
                onClick={handleRespond} 
                disabled={submitting || !response.trim() || !status}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Send className="w-4 h-4 mr-1" />
                )}
                Send Response
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
